import React, { useEffect, useState } from 'react';
import { ChefHat, Check, X, Download, Sparkles, Clock, Flame, ArrowRight, Star, Zap, ShieldCheck, Infinity as InfinityIcon } from 'lucide-react';
import { Footer } from './components/Footer';
import { Testimonials } from './components/Testimonials';
import { AboutCreator } from './components/AboutCreator';
import { ValueBreakdown } from './components/ValueBreakdown';
import { Comparison } from './components/Comparison';
import { TrustBadges } from './components/TrustBadges';
import { Guarantee } from './components/Guarantee';
import { StickyMobileCTA } from './components/StickyMobileCTA';
import { FAQ } from './components/FAQ';
import { FeaturedIn } from './components/FeaturedIn';
import { LanguageGate } from './components/LanguageGate';
import { PaymentMethods } from './components/PaymentMethods';
import { EmailCapturePopup } from './components/EmailCapturePopup';
import { LiveActivity } from './components/LiveActivity';
import { SamplePreview } from './components/SamplePreview';
import { CountdownTimer } from './components/CountdownTimer';
export function App() {
  const [language, setLanguage] = useState<'es' | 'en' | null>(null);
  useEffect(() => {
    const stored = window.localStorage.getItem('rs_lang');
    if (stored === 'es' || stored === 'en') {
      setLanguage(stored);
    }
  }, []);
  const handleSelect = (lang: 'es' | 'en') => {
    window.localStorage.setItem('rs_lang', lang);
    setLanguage(lang);
  };
  if (!language) {
    return <LanguageGate onSelect={handleSelect} data-id="element-0" />;
  }
  return <div className="w-full min-h-screen bg-[#FBF7F0]" data-id="element-1">
      {/* Hero Section */}
      <section className="w-full bg-[#F5F1E8] py-16 md:py-24" data-id="element-2">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-3">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-center" data-id="element-4">
            <div className="order-2 lg:order-1 space-y-6" data-id="element-5">
              <div className="text-2xl md:text-3xl text-[#C2410C] leading-tight" style={{
              fontFamily: 'DM Serif Display, serif'
            }} data-id="element-6">
                Authentic Mexico:{' '}
                <span className="italic text-[#3E2723]" data-id="element-7">
                  The Resort Secrets
                </span>
              </div>
              <div className="inline-flex items-center gap-2 bg-[#C2410C] text-white px-4 py-2 rounded-full text-sm font-medium" data-id="element-8">
                <ChefHat className="w-4 h-4" data-id="element-9" />
                Premium Digital Masterclass
              </div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-tight text-[#3E2723]" data-id="element-10">
                Stop Eating "Fake" Mexican Food. Start Cooking the Resort
                Secrets.
              </h1>
              <h2 className="text-lg sm:text-xl md:text-2xl text-[#3E2723]/80 font-normal" data-id="element-11">
                The 100% Traditional Recipe Map to Recreating Oaxaca, Tulum &
                Cabo Flavors Using Your Local U.S. Grocery Store.
              </h2>
              <div className="flex flex-wrap items-center gap-3 pt-2" data-id="element-12">
                <div className="flex gap-1" data-id="element-13">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" data-id="element-14" />)}
                </div>
                <span className="text-[#3E2723] font-bold" data-id="element-15">4.9/5</span>
                <span className="text-[#3E2723]/70" data-id="element-16">
                  — Trusted by 8,427+ home cooks
                </span>
              </div>
              <div className="pt-2" data-id="element-17">
                <a href="#offer" className="inline-flex items-center gap-2 bg-[#C2410C] hover:bg-[#9A3412] text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors shadow-lg" data-id="element-18">
                  Get The Resort Secrets Now
                  <ArrowRight className="w-5 h-5" data-id="element-19" />
                </a>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 text-sm text-[#3E2723]/70" data-id="element-20">
                  <span className="inline-flex items-center gap-1.5" data-id="element-21">
                    <Zap className="w-4 h-4 text-[#14B8A6]" data-id="element-22" />
                    Instant Download
                  </span>
                  <span className="inline-flex items-center gap-1.5" data-id="element-23">
                    <ShieldCheck className="w-4 h-4 text-[#14B8A6]" data-id="element-24" />
                    30-Day Guarantee
                  </span>
                  <span className="inline-flex items-center gap-1.5" data-id="element-25">
                    <InfinityIcon className="w-4 h-4 text-[#14B8A6]" data-id="element-26" />
                    Lifetime Access
                  </span>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2 relative w-full" data-id="element-27">
              <div className="absolute -inset-3 md:-inset-4 bg-[#C2410C]/15 rounded-3xl blur-2xl" data-id="element-28"></div>
              <img src="https://cdn.magicpatterns.com/uploads/6gEqsvWygQf65T1M4qgoDU/1.png" alt="Authentic Mexican cuisine with mole, guacamole, and traditional ingredients" loading="eager" className="relative block w-full h-auto object-cover rounded-2xl shadow-2xl border-4 border-white" data-id="element-29" />
            </div>
          </div>
        </div>
      </section>

      {/* Opening Hook */}
      <section className="w-full py-16 md:py-24" data-id="element-30">
        <div className="max-w-4xl mx-auto px-6 md:px-12" data-id="element-31">
          <p className="text-2xl md:text-3xl leading-relaxed text-[#3E2723] text-center" data-id="element-32">
            You remember that moment, don't you? Sitting by the turquoise water
            in Tulum or a cobblestone street in Oaxaca, tasting a mole or a fish
            taco that changed everything.
          </p>
          <p className="text-xl md:text-2xl leading-relaxed text-[#3E2723]/70 text-center mt-6" data-id="element-33">
            But when you got home, your "Mexican night" felt flat. Generic. Like
            a cheap imitation from a yellow box. You thought you needed rare
            ingredients or three days of prep time.{' '}
            <span className="text-[#C2410C] font-semibold italic" data-id="element-34">
              You were wrong.
            </span>
          </p>
        </div>
      </section>

      {/* The Problem */}
      <section className="w-full py-16 md:py-24 bg-white" data-id="element-35">
        <div className="max-w-4xl mx-auto px-6 md:px-12" data-id="element-36">
          <h2 className="text-4xl md:text-5xl text-[#3E2723] mb-8" data-id="element-37">
            The Problem
          </h2>
          <p className="text-xl leading-relaxed text-[#3E2723]/80 mb-6" data-id="element-38">
            The problem isn't your cooking skills. It's that most recipes are
            either "Americanized" junk or so complicated you need a professional
            kitchen to make them.
          </p>
          <p className="text-xl leading-relaxed text-[#3E2723]/80" data-id="element-39">
            You miss the authentic smoke, the silkiness of real mole, and the
            "glass-like" crunch of a Baja fish taco. You want paradise back on
            your dinner table, but you don't want to spend all weekend in the
            kitchen.
          </p>
        </div>
      </section>

      {/* Why What You Tried Didn't Work */}
      <section className="w-full py-16 md:py-24" data-id="element-40">
        <div className="max-w-4xl mx-auto px-6 md:px-12" data-id="element-41">
          <h2 className="text-4xl md:text-5xl text-[#3E2723] mb-12" data-id="element-42">
            Why What You Tried Didn't Work
          </h2>
          <div className="space-y-6" data-id="element-43">
            {['Store-bought "taco kits" are loaded with sugar and cumin, tasting nothing like Mexico.', "YouTube recipes often require ingredients you can't find at Walmart or Kroger.", 'Gourmet cookbooks assume you have 12 hours to simmer a sauce.', 'Tex-Mex shortcuts rely on heavy yellow cheese that masks the real traditional flavors.'].map((item, idx) => <div key={idx} className="flex gap-4 items-start" data-id="element-44">
                <X className="w-6 h-6 text-[#C2410C] flex-shrink-0 mt-1" data-id="element-45" />
                <p className="text-lg text-[#3E2723]/80" data-id="element-46">{item}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* The Unique Mechanism */}
      <section className="w-full py-16 md:py-24 bg-[#14B8A6]/5" data-id="element-47">
        <div className="max-w-5xl mx-auto px-6 md:px-12" data-id="element-48">
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border-4 border-[#14B8A6]" data-id="element-49">
            <h2 className="text-4xl md:text-5xl text-[#3E2723] mb-6" data-id="element-50">
              The Unique Mechanism
            </h2>
            <h3 className="text-3xl md:text-4xl text-[#14B8A6] mb-6 italic" data-id="element-51">
              The Supermarket Swap Map™
            </h3>
            <p className="text-xl leading-relaxed text-[#3E2723]/80" data-id="element-52">
              We've decoded the recipes from Mexico's top 5-star resorts and
              translated them for the American kitchen. My Swap Map reveals
              chef-tested substitutions from your everyday supermarket — pantry
              items you'd never guess are the biological twins to Mexico's most
              guarded traditional ingredients.{' '}
              <span className="font-semibold text-[#C2410C]" data-id="element-53">
                100% authentic taste. 0% struggle.
              </span>
            </p>
          </div>
        </div>
      </section>

      <FeaturedIn data-id="element-54" />
      <AboutCreator data-id="element-55" />
      <SamplePreview data-id="element-56" />
      <ValueBreakdown data-id="element-57" />
      <Comparison data-id="element-58" />

      {/* Who This Is For */}
      <section className="w-full py-16 md:py-24" data-id="element-59">
        <div className="max-w-5xl mx-auto px-6 md:px-12" data-id="element-60">
          <div className="grid md:grid-cols-2 gap-12" data-id="element-61">
            <div data-id="element-62">
              <h2 className="text-4xl md:text-5xl text-[#3E2723] mb-8" data-id="element-63">
                Who This Is For
              </h2>
              <div className="space-y-4" data-id="element-64">
                {['Travelers who left a piece of their heart in a Mexican resort.', 'Foodies tired of "yellow cheese" Tex-mex.', 'Busy parents who want a 5-star dinner in 30 minutes.', "Anyone who shops at Walmart, Kroger, Trader Joe's, or Whole Foods."].map((item, idx) => <div key={idx} className="flex gap-3 items-start" data-id="element-65">
                    <Check className="w-5 h-5 text-[#14B8A6] flex-shrink-0 mt-1" data-id="element-66" />
                    <p className="text-lg text-[#3E2723]/80" data-id="element-67">{item}</p>
                  </div>)}
              </div>
            </div>
            <div data-id="element-68">
              <h2 className="text-4xl md:text-5xl text-[#3E2723] mb-8" data-id="element-69">
                Who This Is NOT For
              </h2>
              <div className="space-y-4" data-id="element-70">
                {['People who think Taco Bell is authentic.', 'Professional chefs looking for 3-day fermentation recipes.', 'Anyone not interested in bold, smoky, and vibrant flavors.'].map((item, idx) => <div key={idx} className="flex gap-3 items-start" data-id="element-71">
                    <X className="w-5 h-5 text-[#C2410C] flex-shrink-0 mt-1" data-id="element-72" />
                    <p className="text-lg text-[#3E2723]/80" data-id="element-73">{item}</p>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="w-full py-16 md:py-24 bg-[#EC4899]/5" data-id="element-74">
        <div className="max-w-4xl mx-auto px-6 md:px-12" data-id="element-75">
          <h2 className="text-4xl md:text-5xl text-[#3E2723] mb-12 text-center" data-id="element-76">
            How It Works
          </h2>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-77">
            {[{
            step: '1',
            title: 'Download',
            desc: 'Get the PDF instantly on your phone or tablet.'
          }, {
            step: '2',
            title: 'Shop',
            desc: 'Use the Sourcing Guide at your local grocery store.'
          }, {
            step: '3',
            title: 'Cook',
            desc: 'Follow the "Resort Detail" steps for a guaranteed 5-star result.'
          }].map((item, idx) => <div key={idx} className="text-center" data-id="element-78">
                <div className="w-16 h-16 bg-[#EC4899] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4" data-id="element-79">
                  {item.step}
                </div>
                <h3 className="text-2xl font-semibold text-[#3E2723] mb-3" data-id="element-80">
                  {item.title}
                </h3>
                <p className="text-[#3E2723]/70" data-id="element-81">{item.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      <Testimonials data-id="element-82" />
      <Guarantee data-id="element-83" />
      <FAQ data-id="element-84" />
      <TrustBadges data-id="element-85" />

      {/* Offer + CTA */}
      <section id="offer" className="w-full min-h-screen flex items-center py-12 md:py-16 bg-[#3E2723]" data-id="element-86">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center w-full" data-id="element-87">
          <div className="inline-flex items-center gap-2 bg-red-600 text-white px-5 py-2.5 rounded-full text-sm md:text-base font-extrabold mb-6 animate-pulse shadow-[0_0_25px_rgba(220,38,38,0.7)] border-2 border-yellow-400 uppercase tracking-wide" data-id="element-88">
            <Flame className="w-5 h-5 text-yellow-300 animate-bounce" data-id="element-89" />
            <Clock className="w-4 h-4" data-id="element-90" />
            Hurry! Limited Time Offer — 50% OFF
            <Flame className="w-5 h-5 text-yellow-300 animate-bounce" data-id="element-91" />
          </div>
          <CountdownTimer data-id="element-92" />
          <h2 className="text-4xl md:text-5xl text-white mb-4" data-id="element-93">
            Bring Paradise Home Today.
          </h2>
          <p className="text-lg md:text-xl text-white/90 mb-6 max-w-2xl mx-auto" data-id="element-94">
            Stop settling for "fake" Mexican food. Get the authentic resort
            flavors you crave for less than the price of a single appetizer.
          </p>

          <div className="flex justify-center mb-5" data-id="element-95">
            <div className="relative inline-flex items-center gap-3 bg-yellow-400 text-[#3E2723] px-6 py-3 rounded-xl font-extrabold text-base md:text-lg shadow-[0_0_30px_rgba(250,204,21,0.5)] border-2 border-yellow-300" data-id="element-96">
              <Sparkles className="w-5 h-5" data-id="element-97" />
              VALUED AT $129 USD
              <Sparkles className="w-5 h-5" data-id="element-98" />
              <span className="absolute -top-2 -right-2 flex h-4 w-4" data-id="element-99">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#EC4899] opacity-75" data-id="element-100"></span>
                <span className="relative inline-flex rounded-full h-4 w-4 bg-[#EC4899]" data-id="element-101"></span>
              </span>
            </div>
          </div>
          <div className="relative bg-white/10 backdrop-blur-sm rounded-2xl p-6 md:p-8 mb-5 inline-block border-2 border-[#EC4899]/50" data-id="element-102">
            <div className="absolute -top-3 -right-3 bg-yellow-400 text-[#3E2723] px-3 py-1 rounded-full text-xs font-extrabold rotate-12 shadow-lg flex items-center gap-1" data-id="element-103">
              <Sparkles className="w-3 h-3" data-id="element-104" />
              SAVE $6.51
            </div>
            <div className="flex items-baseline justify-center gap-3 mb-1" data-id="element-105">
              <span className="text-2xl md:text-3xl text-white/50 line-through font-medium" data-id="element-106">
                $12.90
              </span>
              <span className="text-6xl md:text-7xl font-bold text-yellow-300" data-id="element-107">
                $6.39
              </span>
            </div>
            <div className="text-lg text-white/80" data-id="element-108">USD — One-Time Payment</div>
          </div>
          <div className="relative inline-block group" data-id="element-109">
            <span className="absolute inset-0 rounded-xl bg-yellow-300 blur-xl opacity-60 animate-pulse" data-id="element-110"></span>
            <a href="https://foundationalgut.lat/cart/45663549980774:1" className="relative inline-flex items-center gap-3 bg-[#C2410C] hover:bg-[#9A3412] text-white px-10 py-5 rounded-xl text-lg md:text-2xl font-extrabold uppercase tracking-wide shadow-2xl border-4 border-yellow-300 transition-all duration-300 hover:scale-105 hover:shadow-[0_0_40px_rgba(250,204,21,0.8)]" data-id="element-111">
              <Download className="w-6 h-6 md:w-7 md:h-7" data-id="element-112" />
              <span data-id="element-113">Get the Resort Secrets Now</span>
              <ArrowRight className="w-6 h-6 md:w-7 md:h-7 group-hover:translate-x-1 transition-transform" data-id="element-114" />
            </a>
          </div>

          <div className="flex flex-wrap justify-center items-center gap-x-6 gap-y-2 mt-6 text-sm md:text-base text-white/80" data-id="element-115">
            <span className="inline-flex items-center gap-1.5" data-id="element-116">
              <Zap className="w-4 h-4 text-yellow-300" data-id="element-117" />
              Instant Download
            </span>
            <span className="inline-flex items-center gap-1.5" data-id="element-118">
              <ShieldCheck className="w-4 h-4 text-yellow-300" data-id="element-119" />
              30-Day Guarantee
            </span>
            <span className="inline-flex items-center gap-1.5" data-id="element-120">
              <InfinityIcon className="w-4 h-4 text-yellow-300" data-id="element-121" />
              Lifetime Access
            </span>
          </div>

          <PaymentMethods label="Secure payment with" labelClassName="text-xs uppercase tracking-[0.25em] text-yellow-300 font-bold text-center mb-4" className="mt-8 max-w-xl mx-auto" data-id="element-122" />

          <p className="mt-6 max-w-2xl mx-auto text-base md:text-lg leading-relaxed italic font-medium text-[#4ADE80]" data-id="element-123">
            🌿 This recipe book costs less than 3 coffees — but unlike caffeine
            that fades in less than an hour, the knowledge you'll gain will
            serve you for a lifetime. 🌱
          </p>

          <div className="mt-10 max-w-2xl mx-auto bg-white/5 border-l-4 border-yellow-300 rounded-r-lg p-6 text-left" data-id="element-124">
            <p className="text-white/90 text-base md:text-lg leading-relaxed" data-id="element-125">
              <span className="font-bold text-yellow-300" data-id="element-126">P.S. —</span>{' '}
              Remember, you're protected by our 30-day money-back guarantee.
              Download the playbook, cook three recipes, and if you don't taste
              paradise on your plate, email us for a full refund. The only thing
              you can lose is the fake "Mexican night."
            </p>
            <p className="text-white/70 text-sm mt-3 italic" data-id="element-127">
              — Isabella Romero
            </p>
          </div>
        </div>
      </section>

      <Footer data-id="element-128" />
      <StickyMobileCTA data-id="element-129" />
      <LiveActivity data-id="element-130" />
      <EmailCapturePopup data-id="element-131" />
    </div>;
}